/**
 * First-pass verdicts, in the rail, on the campaign you're looking at.
 *
 * Staff only — the query 403s for clients and the card renders nothing. This
 * is the same data as the CM desk, placed where the review actually happens:
 * next to the campaign's content, so the CM reads flags and copies feedback
 * without leaving the page.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileWarning, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { isCoworkLive } from '@/cowork/live/coworkClient';

export function DraftReviewCard({ campaignId }: { campaignId: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const { data: me } = useQuery({
    queryKey: ['cowork', 'cm', 'me'],
    queryFn: () => coworkApi.cm.me().catch(() => ({ staff: false, email: null })),
    enabled: isCoworkLive(),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const staff = me?.staff === true;
  const { data } = useQuery({
    queryKey: ['cowork', 'cm', 'draft-reviews', campaignId],
    queryFn: () => coworkApi.cm.draftReviews(campaignId),
    enabled: staff,
    staleTime: 60_000,
  });

  const reviews = (data?.reviews ?? []).filter(r => r.status === 'flagged' || r.status === 'clean');
  if (!staff || reviews.length === 0) return null;

  const flagged = reviews.filter(r => r.status === 'flagged').length;

  return (
    <div className="mx-4 mb-2 rounded-xl border border-[#2a5fb0]/20 bg-[#2a5fb0]/[0.04] px-3 py-2.5">
      <p className="mb-1.5 flex items-center gap-1.5 text-[12px] text-[#2a5fb0]" style={{ fontWeight: 700 }}>
        <FileWarning className="h-3.5 w-3.5" aria-hidden />
        First-pass review · {flagged ? `${flagged} flagged` : 'all clean'}
      </p>
      <div className="space-y-1">
        {reviews.slice(0, 5).map(r => (
          <div key={r.id} className="rounded-lg bg-white px-2.5 py-1.5">
            <button
              type="button"
              onClick={() => setOpen(open === r.id ? null : r.id)}
              className="flex w-full items-center gap-1.5 text-left"
            >
              <span className="min-w-0 flex-1 truncate text-[12px] text-[#101828]" style={{ fontWeight: 600 }}>
                {r.creatorHandle ?? 'creator'}
              </span>
              <span className={r.status === 'flagged'
                ? 'rounded bg-[#f79009]/10 px-1.5 text-[10px] font-semibold text-[#f79009]'
                : 'rounded bg-[#12b76a]/10 px-1.5 text-[10px] font-semibold text-[#12b76a]'}>
                {r.status === 'flagged' ? `${r.verdict?.flags.length ?? 0} flags` : 'clean'}
              </span>
              {open === r.id ? <ChevronUp className="h-3 w-3 text-gray-400" aria-hidden /> : <ChevronDown className="h-3 w-3 text-gray-400" aria-hidden />}
            </button>
            {open === r.id && r.verdict && (
              <div className="mt-1 border-t border-gray-100 pt-1 text-[11.5px] text-[#475467]">
                <p className="mb-0.5 italic text-[#98a2b3]">Seen: {r.verdict.reviewed}</p>
                {r.verdict.flags.length > 0 && (
                  <ul className="list-disc pl-3.5">{r.verdict.flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
                )}
                {r.verdict.suggestedFeedback && (
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(r.verdict!.suggestedFeedback!)}
                    className="mt-1 inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[10.5px] hover:border-gray-300"
                  >
                    <Copy className="h-2.5 w-2.5" aria-hidden /> Copy suggested feedback
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
